import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  X, Calendar, Clock, Users, MapPin, Video, DollarSign, 
  Loader2, Share2, MessageCircle, Link2, Check
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateEventModal = ({ isOpen, onClose, onEventCreated, sessionTypes = [] }) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [eventData, setEventData] = useState({
    title: '',
    description: '',
    event_type: 'one_to_one',
    date: '',
    start_time: '09:00',
    end_time: '10:00',
    is_free: true,
    price: 0,
    currency: 'CHF',
    max_participants: 1,
    location: 'online',
    meeting_link: '',
    session_type_id: ''
  });
  const [shareToFeed, setShareToFeed] = useState(false);
  const [shareMessage, setShareMessage] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!eventData.title || !eventData.date) return;
    
    setLoading(true);
    try {
      // Build datetime objects
      const startDateTime = new Date(`${eventData.date}T${eventData.start_time}`);
      const endDateTime = new Date(`${eventData.date}T${eventData.end_time}`);
      
      const payload = {
        title: eventData.title,
        description: eventData.description,
        event_type: eventData.event_type,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        is_free: eventData.is_free,
        price: eventData.is_free ? 0 : parseFloat(eventData.price),
        currency: eventData.currency,
        max_participants: eventData.event_type === 'one_to_one' ? 1 : parseInt(eventData.max_participants),
        location: eventData.location,
        meeting_link: eventData.meeting_link
      };
      
      const response = await axios.post(`${API}/events`, payload, { withCredentials: true });
      
      // Share to feed if requested
      if (shareToFeed && response.data.event_id) {
        await axios.post(
          `${API}/events/${response.data.event_id}/share-to-feed`,
          { message: shareMessage || null },
          { withCredentials: true }
        );
      }
      
      onEventCreated(response.data);
      onClose();
      
      // Reset form
      setEventData({
        title: '',
        description: '',
        event_type: 'one_to_one',
        date: '',
        start_time: '09:00',
        end_time: '10:00',
        is_free: true,
        price: 0,
        currency: 'CHF',
        max_participants: 1,
        location: 'online',
        meeting_link: ''
      });
      setStep(1);
      setShareToFeed(false);
      setShareMessage('');
      
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Erreur lors de la création de l\'événement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4 text-white flex items-center justify-between">
          <h2 className="text-lg font-bold">
            {step === 1 ? 'Créer un événement' : 'Partager l\'événement'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {step === 1 ? (
            <div className="space-y-5">
              {/* Pre-fill from Session Type */}
              {sessionTypes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pré-remplir depuis une session</label>
                  <select
                    value={eventData.session_type_id}
                    onChange={(e) => {
                      const st = sessionTypes.find(s => s.session_type_id === e.target.value);
                      if (st) {
                        const endH = parseInt(eventData.start_time.split(':')[0]) + Math.ceil((st.duration || 60) / 60);
                        const endMin = (st.duration || 60) % 60;
                        setEventData({
                          ...eventData,
                          session_type_id: st.session_type_id,
                          title: st.title || '',
                          description: st.description || '',
                          is_free: st.is_free || false,
                          price: st.price || 0,
                          end_time: `${String(Math.min(endH, 23)).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`
                        });
                      } else {
                        setEventData({ ...eventData, session_type_id: '' });
                      }
                    }}
                    className="w-full p-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-purple-300 focus:border-purple-500 outline-none"
                    data-testid="session-type-selector"
                  >
                    <option value="">Créer manuellement</option>
                    {sessionTypes.filter(s => s.is_active !== false).map(st => (
                      <option key={st.session_type_id} value={st.session_type_id}>
                        {st.title} ({st.duration} min{st.is_free ? ' - Gratuit' : ` - ${st.price} CHF`})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Event Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type d'événement</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setEventData({ ...eventData, event_type: 'one_to_one', max_participants: 1 })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      eventData.event_type === 'one_to_one'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Users className="h-6 w-6 text-purple-500 mb-2" />
                    <p className="font-medium text-gray-900">Session 1-to-1</p>
                    <p className="text-xs text-gray-500">Rendez-vous individuel</p>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setEventData({ ...eventData, event_type: 'conference', max_participants: 10 })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      eventData.event_type === 'conference'
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Video className="h-6 w-6 text-orange-500 mb-2" />
                    <p className="font-medium text-gray-900">Conférence</p>
                    <p className="text-xs text-gray-500">Événement de groupe</p>
                  </button>
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Titre *</label>
                <Input
                  value={eventData.title}
                  onChange={(e) => setEventData({ ...eventData, title: e.target.value })}
                  placeholder="Ex: Consultation stratégie marketing"
                  data-testid="event-title"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={eventData.description}
                  onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
                  placeholder="Décrivez votre événement..."
                  rows={3}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-300 focus:border-purple-500 outline-none resize-none"
                />
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="h-4 w-4 inline mr-1" />
                    Date *
                  </label>
                  <Input
                    type="date"
                    value={eventData.date}
                    onChange={(e) => setEventData({ ...eventData, date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="event-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock className="h-4 w-4 inline mr-1" />
                    Début
                  </label>
                  <Input
                    type="time"
                    value={eventData.start_time}
                    onChange={(e) => setEventData({ ...eventData, start_time: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fin</label>
                  <Input
                    type="time"
                    value={eventData.end_time}
                    onChange={(e) => setEventData({ ...eventData, end_time: e.target.value })}
                  />
                </div>
              </div>

              {/* Max participants (for conference) */}
              {eventData.event_type === 'conference' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Users className="h-4 w-4 inline mr-1" />
                    Nombre max de participants
                  </label>
                  <Input
                    type="number"
                    value={eventData.max_participants}
                    onChange={(e) => setEventData({ ...eventData, max_participants: e.target.value })}
                    min="2"
                    max="100"
                  />
                </div>
              )}

              {/* Pricing */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tarification</label>
                <div className="flex items-center gap-4 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={eventData.is_free}
                      onChange={() => setEventData({ ...eventData, is_free: true, price: 0 })}
                      className="w-4 h-4 text-purple-600"
                    />
                    <span className="text-sm">Gratuit</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={!eventData.is_free}
                      onChange={() => setEventData({ ...eventData, is_free: false })}
                      className="w-4 h-4 text-purple-600"
                    />
                    <span className="text-sm">Payant</span>
                  </label>
                </div>
                
                {!eventData.is_free && (
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Input
                        type="number"
                        value={eventData.price}
                        onChange={(e) => setEventData({ ...eventData, price: e.target.value })}
                        placeholder="Prix"
                        min="1"
                      />
                    </div>
                    <select
                      value={eventData.currency}
                      onChange={(e) => setEventData({ ...eventData, currency: e.target.value })}
                      className="px-3 border border-gray-200 rounded-xl"
                    >
                      <option value="CHF">CHF</option>
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Meeting Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Link2 className="h-4 w-4 inline mr-1" />
                  Lien de la réunion (optionnel)
                </label>
                <Input
                  value={eventData.meeting_link}
                  onChange={(e) => setEventData({ ...eventData, meeting_link: e.target.value })}
                  placeholder="https://zoom.us/j/..."
                />
                <p className="text-xs text-gray-500 mt-1">Zoom, Google Meet, Teams...</p>
              </div>
            </div>
          ) : (
            /* Step 2: Share options */
            <div className="space-y-5">
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Événement prêt !</h3>
                <p className="text-gray-500">Souhaitez-vous le partager ?</p>
              </div>

              {/* Share to Feed */}
              <label className="flex items-start gap-3 p-4 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-purple-300 transition-colors">
                <input
                  type="checkbox"
                  checked={shareToFeed}
                  onChange={(e) => setShareToFeed(e.target.checked)}
                  className="w-5 h-5 mt-0.5 text-purple-600 rounded"
                />
                <div>
                  <p className="font-medium text-gray-900">Publier sur l'Actualité</p>
                  <p className="text-sm text-gray-500">Vos abonnés verront l'événement dans leur fil d'actualité</p>
                </div>
              </label>

              {shareToFeed && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message d'accompagnement (optionnel)
                  </label>
                  <textarea
                    value={shareMessage}
                    onChange={(e) => setShareMessage(e.target.value)}
                    placeholder="Ex: Je serai ravi de vous retrouver pour cet événement !"
                    rows={3}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-300 focus:border-purple-500 outline-none resize-none"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 p-4 flex gap-3">
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={onClose} className="flex-1">
                Annuler
              </Button>
              <Button
                onClick={() => setStep(2)}
                disabled={!eventData.title || !eventData.date}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600"
              >
                Continuer
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                Retour
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  'Créer l\'événement'
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateEventModal;
